import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material'
import { ComponentProps } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useMutation } from 'urql'
import { graphql } from '../gql'

interface IFormInput {
  name: string
  website: string
}

const createClientMutation = graphql(`
  mutation AddClientDialogCreateClient($input: CreateClientInput!) {
    CreateClient(input: $input) {
      name
    }
  }
`)

export default function AddClientDialog(props: {
  onClose: () => void
  dialogProps: ComponentProps<typeof Dialog>
}) {
  const [createClientResult, createClient] = useMutation(createClientMutation)

  const { handleSubmit, control, reset } = useForm<IFormInput>({
    defaultValues: {
      name: '',
      website: '',
    },
  })

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    await createClient({
      input: {
        name: data.name,
        website: data.website.trim() === '' ? undefined : data.website,
      },
    }).then((r) => {
      if (!r.error) {
        reset()
        props.onClose()
      }
    })
  }

  return (
    <Dialog
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(onSubmit),
      }}
      onClose={props.onClose}
      {...props.dialogProps}
    >
      <DialogTitle>Add a Client</DialogTitle>
      <DialogContent sx={{ minWidth: 400 }}>
        <Stack spacing={4}>
          <Controller
            name="name"
            control={control}
            rules={{
              required: true,
              maxLength: 255,
              minLength: { value: 3, message: 'Must be longer than 3 characters' },
            }}
            render={({ field, fieldState }) => (
              <TextField
                autoFocus
                required
                margin="dense"
                label="Name"
                fullWidth
                variant="standard"
                {...field}
                disabled={createClientResult.fetching}
                error={!!fieldState.error}
                helperText={fieldState.error && (fieldState.error?.message || 'Required')}
              />
            )}
          />
          <Controller
            name="website"
            control={control}
            rules={{ maxLength: 255 }}
            render={({ field, fieldState }) => (
              <TextField
                margin="dense"
                label="Website"
                fullWidth
                variant="standard"
                {...field}
                disabled={createClientResult.fetching}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />

          {createClientResult.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <p>An error has occurred:</p>
              {createClientResult.error.message}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button type="submit">Create</Button>
      </DialogActions>
    </Dialog>
  )
}
